import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { api, setAuthToken, formatApiError, resetSessionExpiry, RETURN_AFTER_LOGIN_KEY, SESSION_EXPIRED_EVENT } from "@/lib/api";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = none, object = user
  const [error, setError] = useState("");

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/auth/me");
        if (alive) setUser(data);
      } catch (e) {
        if (alive) setUser(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    const onSessionExpired = () => {
      const current = `${window.location.pathname}${window.location.search}${window.location.hash}`;
      if (current && current !== "/login") window.sessionStorage.setItem(RETURN_AFTER_LOGIN_KEY, current);
      setAuthToken(null);
      setError("Your secure session expired while this page was open. Sign in again and you will return here.");
      setUser(false);
    };
    window.addEventListener(SESSION_EXPIRED_EVENT, onSessionExpired);
    return () => window.removeEventListener(SESSION_EXPIRED_EVENT, onSessionExpired);
  }, []);

  const login = useCallback(async (email, password) => {
    setError("");
    try {
      const { data } = await api.post("/auth/login", { email, password });
      resetSessionExpiry();
      setAuthToken(data.access_token);
      setUser(data.user);
      return data.user;
    } catch (e) {
      const msg = formatApiError(e);
      console.error("Login failed:", msg);
      setError(msg);
      throw new Error(msg);
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch (e) {
      console.error("Logout request failed (continuing client-side):", formatApiError(e));
    }
    setAuthToken(null);
    resetSessionExpiry();
    setUser(false);
  }, []);

  const value = useMemo(
    () => ({ user, login, logout, error, setError }),
    [user, login, logout, error]
  );

  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}

export const useAuth = () => useContext(AuthCtx);
